<template>
    <section class="student-payments">
        <div class="container student-payments__container">
            <StudentSidebar :student="student" />

            <main class="student-payments__main">
                <header class="student-payments__topbar">
                    <div>
                        <span class="student-payments__eyebrow">Payments</span>
                        <h1>Payment History</h1>
                        <p>Recent purchased courses are shown here. Open any course to view its full payment details, payment method, invoice, coupon savings, and expiry.</p>
                    </div>

                    <a href="/student/dashboard" class="student-payments__back">
                        <i class="ri-arrow-left-line"></i>
                        Dashboard
                    </a>
                </header>

                <section class="student-payments__search-panel">
                    <div>
                        <span class="student-payments__eyebrow">Purchased courses</span>
                        <h2>Recent Courses</h2>
                    </div>

                    <label class="student-payments__search-box">
                        <i class="ri-search-line"></i>
                        <input
                            v-model="search"
                            type="text"
                            placeholder="Search by course name..."
                        />
                        <button v-if="search" type="button" class="student-payments__clear" @click="search = ''">
                            <i class="ri-close-line"></i>
                        </button>
                    </label>
                </section>

                <section class="student-payments__panel">
                    <div class="student-payments__panel-head">
                        <div>
                            <span class="student-payments__eyebrow">Course list</span>
                            <h2>Payment Overview</h2>
                        </div>

                        <p>
                            Showing {{ pagedCourses.length }} of {{ filteredCourses.length }}
                            course{{ filteredCourses.length === 1 ? '' : 's' }}
                        </p>
                    </div>

                    <div v-if="pagedCourses.length" class="student-payments__grid">
                        <article
                            v-for="course in pagedCourses"
                            :key="course.id"
                            class="student-payments__card"
                        >
                            <div class="student-payments__card-side">
                                <a
                                    :href="courseTargetUrl(course)"
                                    class="student-payments__card-media student-payments__course-link"
                                >
                                    <img :src="course.thumbnailPath" :alt="course.title" />
                                </a>

                                <div class="student-payments__side-panel">
                                    <span class="student-payments__mini-label">Course Access</span>
                                    <strong
                                        class="student-payments__side-status"
                                        :class="`is-${accessBadgeTone(course)}`"
                                    >
                                        {{ accessBadgeLabel(course) }}
                                    </strong>
                                    <small
                                        class="student-payments__side-expiry"
                                        :class="`is-${accessBadgeTone(course)}`"
                                    >
                                        {{ course.latestExpireDateLabel ? `Valid until ${course.latestExpireDateLabel}` : 'Expiry date not set' }}
                                    </small>
                                </div>
                            </div>

                            <div class="student-payments__card-body">
                                <div class="student-payments__card-head">
                                    <div>
                                        <span class="student-payments__mini-label">Purchased Course</span>
                                        <small class="student-payments__support-copy">Payment summary and access details</small>
                                    </div>

                                    <div class="student-payments__orbit-badge" :class="`is-${accessBadgeTone(course)}`" aria-hidden="true">
                                        <span class="student-payments__orbit-core">
                                            <i :class="accessBadgeIcon(course)"></i>
                                            <strong>{{ accessBadgeLabel(course) }}</strong>
                                        </span>
                                    </div>
                                </div>

                                <h3>
                                    <a :href="courseTargetUrl(course)" class="student-payments__title-link">
                                        {{ course.title }}
                                    </a>
                                </h3>

                                <div class="student-payments__card-meta">
                                    <span>{{ course.paymentCount }} payment{{ course.paymentCount === 1 ? '' : 's' }}</span>
                                    <span>Last paid {{ course.latestPurchaseDateLabel || '-' }}</span>
                                    <span>Expires {{ course.latestExpireDateLabel || 'Not set' }}</span>
                                </div>

                                <div class="student-payments__card-bottom">
                                    <div class="student-payments__price-row">
                                        <strong>{{ course.currency }} {{ money(course.latestPaidAmount) }}</strong>
                                        <small v-if="Number(course.latestCouponAmount || 0) > 0">
                                            Coupon save {{ course.currency }} {{ money(course.latestCouponAmount) }}
                                        </small>
                                        <small v-else>No coupon used</small>
                                    </div>

                                    <div class="student-payments__card-actions">
                                        <a :href="course.detailPageUrl" class="student-payments__primary-btn">
                                            Payment Details
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    <div v-else class="student-payments__empty">
                        <i class="ri-bank-card-line"></i>
                        <strong>{{ paymentCourses.length ? 'No purchased course matched your search.' : 'No payment history found.' }}</strong>
                        <span>
                            {{ paymentCourses.length ? 'Try another course name or clear the search.' : 'Your purchased course payment records will appear here automatically.' }}
                        </span>
                    </div>

                    <div v-if="totalPages > 1" class="student-payments__footer">
                        <button type="button" class="student-payments__page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
                            Previous
                        </button>

                        <div class="student-payments__pagination">
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                type="button"
                                class="student-payments__page-number"
                                :class="{ 'is-active': page === currentPage }"
                                @click="goToPage(page)"
                            >
                                {{ page }}
                            </button>
                        </div>

                        <button type="button" class="student-payments__page-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
                            Next
                        </button>
                    </div>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentPaymentHistoryComponent');
const pageSize = 4;

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const paymentCourses = parseJson(root?.dataset.paymentCourses, []);
const search = ref('');
const currentPage = ref(1);

const filteredCourses = computed(() => {
    const keyword = search.value.trim().toLowerCase();

    if (!keyword) {
        return paymentCourses;
    }

    return paymentCourses.filter((course) => course.title?.toLowerCase().includes(keyword));
});

const totalPages = computed(() => {
    return Math.max(1, Math.ceil(filteredCourses.value.length / pageSize));
});

const pagedCourses = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredCourses.value.slice(start, start + pageSize);
});

const visiblePages = computed(() => {
    const total = totalPages.value;
    const page = currentPage.value;
    const start = Math.max(1, page - 1);
    const end = Math.min(total, start + 2);
    const adjustedStart = Math.max(1, end - 2);

    return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
});

watch(search, () => {
    currentPage.value = 1;
});

watch(filteredCourses, () => {
    if (currentPage.value > totalPages.value) {
        currentPage.value = totalPages.value;
    }
});

function goToPage(page) {
    currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
}

function money(value) {
    const amount = Number(value || 0);

    return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function courseTargetUrl(course) {
    return course?.learningUrl || course?.browseUrl || course?.detailPageUrl || '#';
}

function hasActiveAccess(course) {
    return Boolean(course?.learningUrl) && String(course?.statusTone || '').toLowerCase() !== 'expired';
}

function accessBadgeTone(course) {
    return hasActiveAccess(course) ? 'active' : 'expired';
}

function accessBadgeLabel(course) {
    return hasActiveAccess(course) ? 'Active' : 'Expired';
}

function accessBadgeIcon(course) {
    return hasActiveAccess(course) ? 'ri-vip-crown-2-fill' : 'ri-close-circle-fill';
}

function accessBadgeNote(course) {
    if (hasActiveAccess(course)) {
        return course?.latestExpireDateLabel
            ? `Access active until ${course.latestExpireDateLabel}`
            : 'Access is currently active';
    }

    return course?.latestExpireDateLabel
        ? `Access expired on ${course.latestExpireDateLabel}`
        : 'Course access has expired';
}
</script>

<style scoped>
.student-payments {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.08), transparent 32%),
        linear-gradient(180deg, #f5f8fc 0%, #eef2f7 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-payments__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-payments__main {
    min-width: 0;
}

.student-payments__topbar,
.student-payments__search-panel,
.student-payments__panel {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-payments__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.05)),
        #ffffff;
    border-color: #dcecff;
}

.student-payments__topbar h1,
.student-payments__search-panel h2,
.student-payments__panel-head h2,
.student-payments__card h3 {
    margin: 0;
    color: #111827;
    font-weight: 800;
}

.student-payments__topbar h1 {
    font-size: 24px;
}

.student-payments__search-panel h2 {
    font-size: 18px;
    line-height: 1.2;
    white-space: nowrap;
}

.student-payments__topbar p,
.student-payments__panel-head p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.6;
}

.student-payments__eyebrow,
.student-payments__mini-label {
    display: block;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.student-payments__eyebrow {
    margin-bottom: 4px;
}

.student-payments__mini-label {
    letter-spacing: 0.02em;
}

.student-payments__back,
.student-payments__primary-btn,
.student-payments__secondary-btn,
.student-payments__page-btn,
.student-payments__page-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: all 0.2s ease;
}

.student-payments__back,
.student-payments__secondary-btn {
    background: #e7f1ff;
    color: #0d6efd;
}

.student-payments__page-btn,
.student-payments__page-number {
    border: 1px solid #dbe4ef;
    background: #ffffff;
    color: #475569;
}

.student-payments__primary-btn {
    background: #0d6efd;
    color: #ffffff;
}

.student-payments__back:hover,
.student-payments__secondary-btn:hover,
.student-payments__page-btn:hover:not(:disabled),
.student-payments__page-number:hover {
    color: #0b5ed7;
    background: #dbeafe;
    transform: translateY(-1px);
}

.student-payments__primary-btn:hover {
    background: #0b5ed7;
    color: #ffffff;
    transform: translateY(-1px);
}

.student-payments__search-panel {
    display: grid;
    grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
    gap: 18px;
    align-items: center;
    padding: 18px 22px;
    margin-bottom: 18px;
}

.student-payments__search-box {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 10px 0 14px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #f8fafc;
}

.student-payments__search-box:focus-within {
    border-color: #0d6efd;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.12);
}

.student-payments__search-box > i {
    color: #64748b;
    font-size: 18px;
}

.student-payments__search-box input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #111827;
    font-size: 14px;
    font-weight: 700;
}

.student-payments__search-box input::placeholder {
    color: #94a3b8;
    font-weight: 600;
}

.student-payments__clear {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 7px;
    background: #eef6ff;
    color: #0d6efd;
}

.student-payments__panel {
    padding: 22px;
}

.student-payments__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;
}

.student-payments__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.student-payments__card {
    display: grid;
    grid-template-columns: 146px minmax(0, 1fr);
    gap: 18px;
    position: relative;
    overflow: hidden;
    padding: 18px;
    border: 1px solid rgba(203, 213, 225, 0.86);
    border-radius: 10px;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(248, 251, 255, 0.98) 58%, rgba(241, 247, 255, 0.92) 100%);
    box-shadow: 0 22px 54px rgba(15, 23, 42, 0.09);
    transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
}

.student-payments__card::before {
    position: absolute;
    inset: 0;
    content: "";
    pointer-events: none;
    background:
        linear-gradient(90deg, rgba(13, 110, 253, 0.16), rgba(32, 201, 151, 0.1), transparent 42%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.72), transparent 36%);
    opacity: 0.76;
}

.student-payments__card:hover {
    border-color: rgba(13, 110, 253, 0.32);
    box-shadow: 0 28px 70px rgba(15, 23, 42, 0.14);
    transform: translateY(-3px);
}

.student-payments__card-side {
    position: relative;
    z-index: 1;
    display: grid;
    align-content: start;
    gap: 12px;
}

.student-payments__card-media {
    position: relative;
    overflow: hidden;
    z-index: 1;
    border-radius: 10px;
    background: #eff6ff;
    aspect-ratio: 1 / 1;
    align-self: start;
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.16);
}

.student-payments__course-link {
    display: block;
    color: inherit;
    text-decoration: none;
}

.student-payments__card-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scale(1.01);
}

.student-payments__card-media::after {
    position: absolute;
    inset: 0;
    content: "";
    background: linear-gradient(180deg, transparent 46%, rgba(15, 23, 42, 0.2));
}

.student-payments__side-panel {
    display: grid;
    gap: 5px;
    padding: 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.74);
    box-shadow: inset 0 0 0 1px rgba(219, 234, 254, 0.92);
}

.student-payments__side-status {
    font-size: 16px;
    font-weight: 900;
    line-height: 1.1;
    text-transform: uppercase;
}

.student-payments__side-status.is-active {
    color: #15803d;
}

.student-payments__side-status.is-expired {
    color: #dc2626;
}

.student-payments__support-copy {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.5;
}

.student-payments__side-expiry {
    font-size: 12px;
    font-weight: 800;
    line-height: 1.5;
}

.student-payments__side-expiry.is-active,
.student-payments__side-expiry.is-expired {
    color: #dc2626;
}

.student-payments__card-body {
    position: relative;
    z-index: 1;
    display: grid;
    gap: 12px;
    min-width: 0;
}

.student-payments__card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.student-payments__card h3 {
    color: #07111f;
    font-size: 21px;
    line-height: 1.32;
}

.student-payments__title-link {
    color: inherit;
    text-decoration: none;
}

.student-payments__course-link:hover,
.student-payments__title-link:hover {
    color: #0d6efd;
}

.student-payments__card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.student-payments__card-meta span {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
    color: #334155;
    box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.78);
    font-size: 12px;
    font-weight: 700;
}

.student-payments__price-row {
    display: grid;
    gap: 4px;
    align-content: start;
    min-width: 0;
}

.student-payments__price-row strong {
    color: #06111f;
    font-size: 23px;
    font-weight: 900;
}

.student-payments__price-row small {
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.student-payments__card-bottom {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    align-items: stretch;
    margin-top: 4px;
}

.student-payments__card-actions {
    display: grid;
    width: min(100%, 184px);
    justify-self: start;
}

.student-payments__card-actions .student-payments__primary-btn {
    width: 100%;
    min-height: 44px;
    padding-inline: 14px;
    border-radius: 9px;
}

.student-payments__card-actions .student-payments__primary-btn {
    box-shadow: 0 12px 26px rgba(13, 110, 253, 0.24);
}

.student-payments__orbit-badge {
    position: relative;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 82px;
    height: 82px;
    border-radius: 50%;
    padding: 5px;
}

.student-payments__orbit-badge::before {
    position: absolute;
    inset: -12px;
    content: "";
    border-radius: 50%;
    opacity: 0.78;
    filter: blur(8px);
}

.student-payments__orbit-badge::after {
    position: absolute;
    inset: 0;
    content: "";
    border-radius: 50%;
}

.student-payments__orbit-badge.is-active::before {
    background: radial-gradient(circle, rgba(34, 197, 94, 0.24) 0%, rgba(255, 255, 255, 0) 72%);
}

.student-payments__orbit-badge.is-active::after {
    background: conic-gradient(from 210deg, #22c55e 0%, #84cc16 38%, #facc15 70%, #22c55e 100%);
}

.student-payments__orbit-badge.is-expired::before {
    background: radial-gradient(circle, rgba(239, 68, 68, 0.22) 0%, rgba(255, 255, 255, 0) 72%);
}

.student-payments__orbit-badge.is-expired::after {
    background: conic-gradient(from 210deg, #ef4444 0%, #f97316 48%, #ef4444 100%);
}

.student-payments__orbit-core {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    gap: 4px;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #ffffff;
    text-align: center;
    box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.92);
}

.student-payments__orbit-core i {
    font-size: 22px;
    line-height: 1;
}

.student-payments__orbit-core strong {
    font-size: 11px;
    font-weight: 900;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.student-payments__orbit-badge.is-active .student-payments__orbit-core i,
.student-payments__orbit-badge.is-active .student-payments__orbit-core strong {
    color: #65a30d;
}

.student-payments__orbit-badge.is-expired .student-payments__orbit-core i,
.student-payments__orbit-badge.is-expired .student-payments__orbit-core strong {
    color: #dc2626;
}

.student-payments__empty {
    display: grid;
    justify-items: center;
    gap: 10px;
    padding: 36px 18px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    background: #f8fbff;
    text-align: center;
}

.student-payments__empty i {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 28px;
}

.student-payments__empty strong {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.student-payments__empty span {
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
}

.student-payments__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 20px;
}

.student-payments__pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.student-payments__page-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}

.student-payments__page-number.is-active {
    border-color: #0d6efd;
    background: #0d6efd;
    color: #ffffff;
}

@media (max-width: 1199px) {
    .student-payments__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .student-payments__card {
        grid-template-columns: 136px minmax(0, 1fr);
    }

    .student-payments__card-bottom {
        grid-template-columns: 1fr;
        align-items: stretch;
    }
}

@media (max-width: 991px) {
    .student-payments {
        padding: 24px 0 36px;
    }

    .student-payments__container,
    .student-payments__search-panel,
    .student-payments__grid {
        grid-template-columns: 1fr;
    }

    .student-payments__orbit-badge {
        width: 74px;
        height: 74px;
    }
}

@media (max-width: 767px) {
    .student-payments__topbar,
    .student-payments__panel-head,
    .student-payments__footer,
    .student-payments__card,
    .student-payments__card-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-payments__card {
        grid-template-columns: 1fr;
    }

    .student-payments__card-media {
        width: min(100%, 220px);
    }

    .student-payments__card-bottom {
        grid-template-columns: 1fr;
    }

    .student-payments__footer {
        align-items: stretch;
    }

    .student-payments__pagination {
        justify-content: flex-start;
    }
}

@media (max-width: 575px) {
    .student-payments__container {
        padding: 0 10px;
    }

    .student-payments__topbar,
    .student-payments__search-panel,
    .student-payments__panel {
        padding: 16px;
    }

    .student-payments__back,
    .student-payments__primary-btn,
    .student-payments__secondary-btn,
    .student-payments__page-btn {
        width: 100%;
    }

    .student-payments__card-actions {
        width: 100%;
        justify-self: stretch;
    }

    .student-payments__card-bottom {
        grid-template-columns: 1fr;
    }
}
</style>
